/**
 * Newest resolve/unresolve wins over a feed or cache row that was read earlier.
 * The acting client notes the click immediately; spectators note hazard_updated.
 * A feed response keeps the stamp when the request started before that note.
 */

type Stamp = { resolved: boolean; at: number };

const stamps = new Map<string, Stamp>();

export function noteHazardResolved(id: string, resolved: boolean, at: number = Date.now()): void {
  if (!id) return;
  const prev = stamps.get(id);
  if (prev && prev.at > at) return;
  stamps.set(id, { resolved, at });
}

export function stampedResolved(id: string): boolean | undefined {
  return stamps.get(id)?.resolved;
}

/** Stamp wins when it was recorded at or after `startedAt`. */
export function newerResolvedThan(id: string, startedAt: number): boolean | undefined {
  const stamp = stamps.get(id);
  if (!stamp || stamp.at < startedAt) return undefined;
  return stamp.resolved;
}

function asResolvedFlag(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (value === 0 || value === 1) return value === 1;
  return undefined;
}

/** Keep a resolve/unresolve that happened after this feed request started. */
export function patchFeedResolved(rows: Array<Record<string, unknown>>, startedAt: number): void {
  for (const row of rows) {
    const id = typeof row.id === 'string' ? row.id : '';
    if (!id) continue;
    const newer = newerResolvedThan(id, startedAt);
    if (newer !== undefined) {
      row.resolved = newer;
      continue;
    }
    const serverResolved = asResolvedFlag(row.resolved);
    if (serverResolved !== undefined) {
      noteHazardResolved(id, serverResolved, startedAt);
    }
  }
}

export function overlayResolved<T extends { id: string; resolved?: boolean }>(hazard: T): T {
  const stamp = stamps.get(hazard.id);
  if (!stamp || !!hazard.resolved === stamp.resolved) return hazard;
  return { ...hazard, resolved: stamp.resolved };
}
